import React, { useState, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, RoundedBox, shaderMaterial } from "@react-three/drei";
import * as THREE from "three";
import {
  Form,
  Input,
  Row,
  Col,
  InputNumber,
  DatePicker,
  Button,
  notification,
} from "antd";

function createFaceTexture(
  text: string,
  canvasWidth: number,
  canvasHeight: number
) {
  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth * 2; // Increase resolution to reduce distortion
  canvas.height = canvasHeight * 2;
  const context = canvas.getContext("2d");

  // console.log("text", text, canvas.width, canvas.height);
  if (context) {
    // Background color
    context.fillStyle = "white";
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Simple border lines
    context.strokeStyle = "#565656";
    context.setLineDash([5, 5]);
    context.lineWidth = 3;

    // Text styling
    context.fillStyle = "#565656";
    context.font = "30px Arial";
    context.textAlign = "center";
    context.textBaseline = "middle";

    // Draw simple "X" pattern with larger spacing
    const spacing = 200;
    const gapSize = 70;

    for (let x = 0; x < canvas.width; x += spacing) {
      for (let y = 0; y < canvas.height; y += spacing) {
        // First half of "X"
        context.beginPath();
        context.moveTo(x, y);
        context.lineTo(
          x + (spacing - gapSize) / 2,
          y + (spacing - gapSize) / 2
        );
        context.moveTo(
          x + spacing - (spacing - gapSize) / 2,
          y + spacing - (spacing - gapSize) / 2
        );
        context.lineTo(x + spacing, y + spacing);
        context.stroke();

        // Second half of "X"
        context.beginPath();
        context.moveTo(x + spacing, y);
        context.lineTo(
          x + spacing - (spacing - gapSize) / 2,
          y + (spacing - gapSize) / 2
        );
        context.moveTo(
          x + (spacing - gapSize) / 2,
          y + spacing - (spacing - gapSize) / 2
        );
        context.lineTo(x, y + spacing);
        context.stroke();

        // Centered text inside the "X" gap
        context.fillText(text, x + spacing / 2, y + spacing / 2);
      }
    }
  }

  // if (context) {
  //   context.fillStyle = "white";
  //   context.fillRect(0, 0, canvas.width, canvas.height);
  //   context.strokeStyle = "black";
  //   context.lineWidth = 5;
  //   context.font = "48px Arial";
  //   context.textAlign = "center";
  //   context.textBaseline = "middle";
  //   context.fillStyle = "black";

  //   // Draw a checker pattern and add label text for each face
  //   const cellSize = canvas.width / 10;
  //   for (let y = 0; y < canvas.height; y += cellSize) {
  //     for (let x = 0; x < canvas.width; x += cellSize) {
  //       context.fillStyle = (x + y) % (2 * cellSize) === 0 ? "gray" : "white";
  //       context.fillRect(x, y, cellSize, cellSize);
  //     }
  //   }
  //   context.fillStyle = "black";
  //   context.fillText(text, canvas.width / 2, canvas.height / 2);
  // }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(1, 1);

  return texture;
}

function CustomBox({
  width,
  height,
  length,
}: {
  width: number;
  height: number;
  length: number;
}) {
  const rightTexture = useMemo(
    () => createFaceTexture("Tegral", length * 100, height * 100),
    [length, height]
  );
  const leftTexture = useMemo(
    () => createFaceTexture("Tegral", length * 100, height * 100),
    [length, height]
  );
  const topTexture = useMemo(
    () => createFaceTexture("Tegral", width * 100, length * 100),
    [width, length]
  );
  const bottomTexture = useMemo(
    () => createFaceTexture("Tegral", width * 100, length * 100),
    [width, length]
  );
  const frontTexture = useMemo(
    () => createFaceTexture("Tegral", width * 100, height * 100),
    [width, height]
  );
  const backTexture = useMemo(
    () => createFaceTexture("Tegral", width * 100, height * 100),
    [width, height]
  );

  const materials = [
    new THREE.MeshPhysicalMaterial({
      map: rightTexture,
      roughness: 0.3,
      metalness: 0.1,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
    }),
    new THREE.MeshPhysicalMaterial({
      map: leftTexture,
      roughness: 0.3,
      metalness: 0.1,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
    }),
    new THREE.MeshPhysicalMaterial({
      map: topTexture,
      roughness: 0.3,
      metalness: 0.1,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
    }),
    new THREE.MeshPhysicalMaterial({
      map: bottomTexture,
      roughness: 0.3,
      metalness: 0.1,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
    }),
    new THREE.MeshPhysicalMaterial({
      map: frontTexture,
      roughness: 0.3,
      metalness: 0.1,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
    }),
    new THREE.MeshPhysicalMaterial({
      map: backTexture,
      roughness: 0.3,
      metalness: 0.1,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
    }),
  ];

  // Apply segments to simulate rounded edges
  return (
    <mesh
      geometry={new THREE.BoxGeometry(width, height, length, 200, 200, 200)}
      material={materials}
      castShadow
      receiveShadow
    />
  );
}

function App() {
  const [width, setWidth] = useState(10 / 5);
  const [height, setHeight] = useState(10 / 5);
  const [length, setLength] = useState(10 / 5);
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const onFinish = (values: any) => {
    console.log("values", values);
    const { vWidth, vHeight, vLength } = values;
    setWidth(vWidth);
    setHeight(vHeight);
    setLength(vLength);
  };

  const gf = (f: any) => form.getFieldValue(f);

  const handleApplyToVisuality = async () => {
    try {
      await form.validateFields();
      const vWidth = gf("width");
      const vHeight = gf("height");
      const vLength = gf("length");
      setWidth(vWidth / 500);
      setHeight(vHeight / 500);
      setLength(vLength / 500);
    } catch (err) {
      console.log(err);
    }
  };

  const validateDimensions = (_: any, value: number) => {
    const { length, width, height } = form.getFieldsValue();

    // Ensure all values are numbers or default to 0
    const lengthValue = length ?? 0;
    const widthValue = width ?? 0;
    const heightValue = height ?? 0;

    // Rule 1: Each field must not exceed 2400
    if (value > 2400) {
      return Promise.reject(
        new Error("Each dimension must not exceed 2400mm.")
      );
    }

    // Rule 2: If one field is > 1200 (but ≤ 2400), the other two must be ≤ 1200
    const fields = [lengthValue, widthValue, heightValue];
    const exceedingFields = fields.filter((field) => field > 1200);

    if (exceedingFields.length > 1) {
      return Promise.reject(
        new Error(
          "If one dimension is greater than 1200mm, the other two dimensions must be ≤ 1200mm."
        )
      );
    }

    return Promise.resolve();
  };

  const openNotification = (pauseOnHover: boolean) => {
    api.success({
      message: "Your request has been submitted!",
      description:
        "Thank you for submitting your request. We're delighted to serve you and will reach out to you soon.",
      showProgress: true,
      pauseOnHover,
    });
  };
  return (
    <>
      {contextHolder}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundImage: "linear-gradient(to bottom right, #fbaf70, #e88268)",
          height: "100vh",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: 2,
            border: "1px solid rgb(197 197 197)",
            boxShadow: "#94949445 2px 2px 10px",
            borderRadius: "20px",
            background: "white",
            boxSizing: "border-box",
            padding: "30px 25px",
            maxWidth: "350px",
            maxHeight: "calc(100vh - 40px)",
            overflowY: "scroll",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            style={{ maxWidth: "600px", margin: "0 auto" }}
            initialValues={{ height: 1000, width: 1000, length: 1000 }}
            onFinish={onFinish}
          >
            <Row gutter={[0, 4]}>
              {" "}
              {/* Adjust gutter for vertical spacing */}
              {/* Qty Field */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="qtyOfCrates"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Qty of Crates Required:</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="qtyOfCrates"
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      max: 1000000,
                      message: "Please enter valid qty (1-1000000)",
                    },
                  ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <InputNumber
                    id="qtyOfCrates"
                    placeholder=""
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              {/* Length Field */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="length"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Length Required (mm):</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="length"
                  rules={[
                    { required: true, message: "Length is required!" },
                    { validator: validateDimensions },
                  ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <InputNumber
                    required
                    id="length"
                    placeholder=""
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              {/* Width Field */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="width"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Width Required (mm):</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="width"
                  rules={[
                    { required: true, message: "Width is required!" },
                    { validator: validateDimensions },
                  ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <InputNumber
                    required
                    id="width"
                    placeholder=""
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              {/* Height Field */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="width"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Height Required (mm):</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="height"
                  rules={[
                    { required: true, message: "Height is required!" },
                    { validator: validateDimensions },
                  ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <InputNumber
                    required
                    id="height"
                    placeholder=""
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={24} style={{ marginBottom: "25px" }}>
                <Button
                  type="dashed"
                  style={{ width: "100%" }}
                  onClick={() => handleApplyToVisuality()}
                >
                  Apply to Visuality
                </Button>
              </Col>
              {/* Weight Rating Field */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="weight"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Weight Rating (kg):</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="weight"
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      max: 100,
                      message: "Please enter valid wight rating (1-100)",
                    },
                  ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <InputNumber
                    id="weight"
                    placeholder=""
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              {/* Date Field */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="date"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Date Required:</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="date"
                  // rules={[
                  //   {
                  //     type: "date",
                  //     message: "Please enter valid height (50-500)",
                  //   },
                  // ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <DatePicker id="date" placeholder="" />
                </Form.Item>
              </Col>
              {/* Your Name */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="yourName"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Your Name:</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="yourName"
                  // rules={[
                  //   {
                  //     type: "date",
                  //     message: "Please enter valid height (50-500)",
                  //   },
                  // ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <Input id="yourName" placeholder="" />
                </Form.Item>
              </Col>
              {/* Business Name */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="businessName"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Business Name:</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="businessName"
                  // rules={[
                  //   {
                  //     type: "date",
                  //     message: "Please enter valid height (50-500)",
                  //   },
                  // ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <Input id="businessName" placeholder="" />
                </Form.Item>
              </Col>
              {/* Address for Delivery */}
              <Col span={14} style={{ display: "flex", alignItems: "center" }}>
                <Form.Item
                  label={
                    <label
                      htmlFor="addressForDelivery"
                      style={{ whiteSpace: "pre", marginTop: "4px" }}
                    >
                      <b>Address for Delivery:</b>
                    </label>
                  }
                  colon={false}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                />
              </Col>
              <Col span={10}>
                <Form.Item
                  name="addressForDelivery"
                  // rules={[
                  //   {
                  //     type: "date",
                  //     message: "Please enter valid height (50-500)",
                  //   },
                  // ]}
                  style={{ marginBottom: "8px" }} // Reduce space between fields
                >
                  <Input id="addressForDelivery" placeholder="" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Col span={24} style={{ marginBottom: "25px" }}>
            <Button
              type="primary"
              style={{ width: "100%" }}
              loading={isLoading}
              onClick={() => {
                setIsLoading(true);
                setTimeout(() => {
                  openNotification(true);
                  form.resetFields();
                  setHeight(10 / 5);
                  setWidth(10 / 5);
                  setLength(10 / 5);
                  setIsLoading(false);
                }, 2000);
              }}
            >
              Submit
            </Button>
          </Col>
        </div>
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            left: "-10%",
          }}
        >
          <Canvas shadows camera={{ position: [4, 4, 4], fov: 50 }}>
            <ambientLight intensity={5.5} position={[1, -5, 5]} />

            <pointLight
              position={[5, 5, 5]} // Position it to create oblique lighting effects
              intensity={0.1} // Strong intensity for noticeable highlights
              castShadow // Enable shadows
              shadow-mapSize-width={1024} // High shadow map resolution for quality shadows
              shadow-mapSize-height={1024}
            />
            <CustomBox width={width} height={height} length={length} />
            <OrbitControls
              enableZoom={false}
              enableDamping={true}
              dampingFactor={0.1}
              maxPolarAngle={Math.PI}
              minPolarAngle={0}
            />
          </Canvas>
        </div>
      </div>
    </>
  );
}

export default App;
